import { Component, Input } from '@angular/core';
import { agreementScheduledPayments } from '../agreement-scheduled-payments';

@Component({
  selector: 'app-agreement-template-sydney-premium',
  templateUrl: './agreement-template-sydney-premium.component.html',
  styleUrls: ['./agreement-template-sydney-premium.component.css']
})
export class AgreementTemplateSydneyPremiumComponent {
  @Input() entityData: any;
  @Input() studentAccountEntityData: any;
  @Input() studioInformationConfig: any;
  @Input() enrollmentForm: any;
  @Input() isTaxInclusive = false;
  @Input() displaySignatures = true;
  @Input() displaySigned = true;
  @Input() _enrollmentEntityData: any;

  get primaryContact(): any {
    const contacts = this.studentAccountEntityData?.field_contacts;
    if (!contacts) return null;
    if (Array.isArray(contacts)) {
      const active = contacts.filter(c => c.field_archived !== '1' && c.field_archived !== 1);
      return active[0] || contacts[0] || null;
    }
    return contacts;
  }

  calculateTotalTuition(): number {
    return this.entityData?.field_enrollment_total_price || 0;
  }

  getOriginalPackageCost(): number {
    if (this.entityData?.field_enrollment_lesson_price && this.entityData?.field_enrollment_lesson_count) {
      return this.entityData.field_enrollment_lesson_price * this.entityData.field_enrollment_lesson_count;
    }
    return 0;
  }

  getDiscount(): number {
    return this.entityData?.field_discount || 0;
  }

  getNotes(): string {
    const notes = this.entityData?.field_notes;
    if (!notes) return '';
    if (typeof notes === 'string') return notes;
    if (Array.isArray(notes)) return notes[0]?.value || '';
    if (typeof notes === 'object' && 'value' in notes) return notes.value || '';
    return '';
  }

  get enrollmentTypeState() {
    const category = this.entityData?.field_category;
    const targetId = Array.isArray(category) ? category[0]?.target_id?.toString() : category?.target_id?.toString() || category?.toString();

    const map: { [key: string]: string } = {
      '50': 'original', '49': 'original',
      '51': 'extension', '543': 'extension', '546': 'extension',
      '65': 'renewal',
      '5': 'service', '66': 'service', '67': 'service', '68': 'service',
      '69': 'service', '542': 'service', '1373': 'service', '1395': 'service',
      '1421': 'original', '70': 'original'
    };

    const mapped = targetId ? map[targetId] : null;
    return {
      original: mapped === 'original',
      extension: mapped === 'extension',
      renewal: mapped === 'renewal',
      service: mapped === 'service' || mapped === null
    };
  }

  get isInstallmentAgreement(): boolean {
    const structure = this.enrollmentForm?.get?.('field_payments_structure')?.value
      || this.entityData?.field_payments_structure;
    return structure && structure !== 'paid_in_full';
  }

  /** Scheduled payment rows normalized to {amount, date, down}. */
  private scheduledPaymentRows(): { amount: number; date: string | null; down: boolean }[] {
    let raw = agreementScheduledPayments(this.enrollmentForm?.get?.('field_scheduled_payments')?.value);
    if (raw.length === 0) {
      raw = agreementScheduledPayments(this.entityData?.field_scheduled_payments);
    }

    return raw
      .map(r => ({
        amount: parseFloat(this.fieldValue(r, 'field_payment_amount')) || 0,
        date: this.fieldValue(r, 'field_payment_date') || null,
        down: ['1', 1, true].includes(this.fieldValue(r, 'field_down_payment'))
      }))
      .filter(r => r.amount > 0)
      .sort((x, y) => !x.date ? 1 : !y.date ? -1 : x.date.localeCompare(y.date));
  }

  getDownPaymentAmount(): number {
    const down = this.scheduledPaymentRows().find(r => r.down);
    return down?.amount || 0;
  }

  /** All scheduled payments for the disclosure table (down payment included, US-style). */
  get allPaymentRows(): { amount: number; date: string | null; down: boolean }[] {
    return this.scheduledPaymentRows();
  }

  areAllPaymentAmountsSame(): boolean {
    const rows = this.scheduledPaymentRows();
    if (rows.length <= 1) return true;
    return rows.every(r => Math.abs(r.amount - rows[0].amount) < 0.01);
  }

  get firstPaymentDate(): string | null {
    return this.scheduledPaymentRows()[0]?.date || null;
  }

  get earlyPaymentPenalty(): boolean {
    const v = this.fieldValue(this.entityData, 'field_early_payment_penalty');
    return v === true || v === 1 || v === '1' || v === 'true';
  }

  get earlyPaymentRefund(): boolean {
    // Default to true; only an explicit false disables the refund entitlement.
    const v = this.fieldValue(this.entityData, 'field_early_payment_refund');
    return !(v === false || v === 'false' || v === 0 || v === '0');
  }

  get expirationDate(): any {
    const formVal = this.enrollmentForm?.get?.('field_expiration_date')?.value;
    const v = (formVal !== undefined && formVal !== null && formVal !== '')
      ? formVal
      : this.fieldValue(this.entityData, 'field_expiration_date');
    if (v && typeof v === 'object' && typeof v.toDate === 'function') return v.toDate();
    return v || null;
  }

  private fieldValue(obj: any, fieldName: string): any {
    const v = obj?.[fieldName];
    if (Array.isArray(v)) return v[0]?.value ?? v[0];
    if (v && typeof v === 'object' && 'value' in v) return v.value;
    return v;
  }
}
